// src/pages/Auth/AuthForm.jsx

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import styles from './AuthForm.module.css';
import { useNavigate } from 'react-router-dom';
import { showSuccess, showError } from '../../../../components/ModernSnackbar';
import { login } from '../../../../services/api/fytnServices';
import { FaEye, FaEyeSlash, FaEnvelope, FaLock, FaUser } from 'react-icons/fa';
import Cookies from 'js-cookie';
import ForgotPassword from './ForgotPassword';

// Password validation schema
const passwordSchema = yup
  .string()
  .required("Password is required")
  .min(8, "Minimum 8 characters")
  .matches(/[A-Z]/, "Must include one uppercase letter")
  .matches(/[a-z]/, "Must include one lowercase letter")
  .matches(/[0-9]/, "Must include one number")
  .matches(/[@$!%*#?&]/, "Must include one special character");

// Login schema
const loginSchema = yup.object().shape({
  email: yup.string().email("Invalid email").required("Email is required"),
  password: passwordSchema,
});

const AuthForm = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(loginSchema),
  });

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      const response = await login(data);
      const user = response?.data;
      // Store token and user in cookies
      Cookies.set('fytn_token', user.token, { expires: 7 }); // 7 days expiry
      Cookies.set('fytn_user', JSON.stringify(user.user), { expires: 7 });
      if (user.user.status === 'ACTIVE') {
        showSuccess('Login Successful');
        reset()
        if(user.user.userRole === 'ADMIN') {
        navigate('/admin');
      }
      else{
        navigate('/dashboard');
      }
    } 
      else {
        showError('User not allowed. Please contact support.');
      }
    } catch (error) {
      console.log('Login error:', error);
      reset()
    
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.authContainer}>
      <div className={styles.backgroundDecoration}>
        <div className={styles.circle1}></div>
        <div className={styles.circle2}></div>
        <div className={styles.circle3}></div>
        <div className={styles.circle4}></div>
        <div className={styles.circle5}></div>
      </div>
      
      <div className={styles.formContainer}>
        <div className={styles.logoSection}>
          <img src="/logo.png" alt="FYTN Logo" className={styles.logoImage} />
          <h1>Welcome Back</h1>
          <p>Sign in to your account to continue</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
          <div className={styles.inputGroup}>
            <div className={styles.inputWrapper}>
              <FaEnvelope className={styles.inputIcon} />
              <input 
                {...register("email")} 
                placeholder="Enter your email"
                className={errors.email ? styles.inputError : ''}
              />
            </div>
            {errors.email && <span className={styles.errorText}>{errors.email?.message}</span>}
          </div>

          <div className={styles.inputGroup}>
            <div className={styles.inputWrapper}>
              <FaLock className={styles.inputIcon} />
              <input
                type={showPassword ? "text" : "password"}
                {...register("password")}
                placeholder="Enter your password"
                className={errors.password ? styles.inputError : ''}
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className={styles.eyeIcon}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            {errors.password && <span className={styles.errorText}>{errors.password?.message}</span>}
          </div>
      
          <button 
            className={`${styles.btn} ${isLoading ? styles.btnLoading : ''}`} 
            type="submit"
            disabled={isLoading}
          >
            {isLoading ? (
              <div className={styles.loader}>
                <div className={styles.spinner}></div>
                <span>Signing in...</span>
              </div>
            ) : (
              'Sign In'
            )}
          </button>
          
          <div className={styles.forgotPassword}>
            <button
              type="button"
              onClick={() => setForgotOpen(true)}
              className={styles.forgotLink}
            >
              Forgot your password?
            </button>
          </div>
        </form>


      </div>
      
      <ForgotPassword open={forgotOpen} onClose={() => setForgotOpen(false)} />
    </div>
  );
};

export default AuthForm;
